import { useState, useMemo } from "react";
import type { TradingRoutine, RoutineCompletion } from "wasp/entities";
import { cn } from "../client/utils";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../client/components/ui/card";
import { Button } from "../client/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../client/components/ui/select";
import { ChevronLeft, ChevronRight } from "lucide-react";
import useTranslation from "../client/hooks/useTranslation";

interface GlobalRoutineHeatmapProps {
  routines: (TradingRoutine & { routineCompletions: RoutineCompletion[] })[];
}

type DayData = {
  date: Date;
  totalCompletions: number;
  totalExpected: number;
  completionRate: number; // 0-1, based on completions vs expected
};

type ViewType = "month" | "quarter";

export default function GlobalRoutineHeatmap({
  routines,
}: GlobalRoutineHeatmapProps) {
  const t = useTranslation();
  const [viewType, setViewType] = useState<ViewType>("month");
  const [currentPeriod, setCurrentPeriod] = useState(() => {
    const now = new Date();
    return {
      year: now.getFullYear(),
      month: now.getMonth(),
      quarter: Math.floor(now.getMonth() / 3),
    };
  });

  const heatmapData = useMemo(() => {
    return generateGlobalHeatmapData(
      routines,
      viewType,
      currentPeriod.year,
      currentPeriod.month,
      currentPeriod.quarter,
    );
  }, [routines, viewType, currentPeriod]);

  const activeRoutines = routines.filter((r) => r.isActive);
  if (activeRoutines.length === 0) {
    return null;
  }

  const navigatePeriod = (direction: "prev" | "next") => {
    setCurrentPeriod((prev) => {
      const newPeriod = { ...prev };
      if (viewType === "month") {
        if (direction === "next") {
          newPeriod.month++;
          if (newPeriod.month > 11) {
            newPeriod.month = 0;
            newPeriod.year++;
          }
        } else {
          newPeriod.month--;
          if (newPeriod.month < 0) {
            newPeriod.month = 11;
            newPeriod.year--;
          }
        }
      } else {
        // quarter
        if (direction === "next") {
          newPeriod.quarter++;
          if (newPeriod.quarter > 3) {
            newPeriod.quarter = 0;
            newPeriod.year++;
          }
        } else {
          newPeriod.quarter--;
          if (newPeriod.quarter < 0) {
            newPeriod.quarter = 3;
            newPeriod.year--;
          }
        }
        // Update month to match quarter
        newPeriod.month = newPeriod.quarter * 3;
      }
      return newPeriod;
    });
  };

  const getPeriodLabel = () => {
    if (viewType === "month") {
      const monthNames = t.routines.monthNames || [
        "January", "February", "March", "April", "May", "June",
        "July", "August", "September", "October", "November", "December"
      ];
      return `${monthNames[currentPeriod.month]} ${currentPeriod.year}`;
    } else {
      const quarterNames = ["Q1", "Q2", "Q3", "Q4"];
      return `${quarterNames[currentPeriod.quarter]} ${currentPeriod.year}`;
    }
  };

  return (
    <Card className="mb-6">
      <CardHeader>
        <CardTitle className="text-lg">{t.routines.heatmapTitle}</CardTitle>
        <p className="text-sm text-muted-foreground">
          {t.routines.heatmapSubtitle}
        </p>
      </CardHeader>
      <CardContent>
        <div className="mb-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigatePeriod("prev")}
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <span className="font-medium min-w-[150px] text-center">
              {getPeriodLabel()}
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigatePeriod("next")}
              disabled={
                viewType === "month"
                  ? currentPeriod.year === new Date().getFullYear() &&
                    currentPeriod.month === new Date().getMonth()
                  : currentPeriod.year === new Date().getFullYear() &&
                    currentPeriod.quarter ===
                      Math.floor(new Date().getMonth() / 3)
              }
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
          <div className="flex items-center gap-2">
            <Select
              value={viewType}
              onValueChange={(value: ViewType) => {
                setViewType(value);
                const now = new Date();
                setCurrentPeriod({
                  year: now.getFullYear(),
                  month: now.getMonth(),
                  quarter: Math.floor(now.getMonth() / 3),
                });
              }}
            >
              <SelectTrigger className="w-[120px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="month">{t.routines.month}</SelectItem>
                <SelectItem value="quarter">{t.routines.quarter}</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="mb-4 flex items-center justify-end text-xs text-muted-foreground">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-1">
              <div className="h-3 w-3 rounded bg-green-600" />
              <span>{t.routines.excellent}</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="h-3 w-3 rounded bg-green-400" />
              <span>{t.routines.good}</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="h-3 w-3 rounded bg-green-300" />
              <span>{t.routines.partial}</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="h-3 w-3 rounded bg-gray-200" />
              <span>{t.routines.notDue}</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="h-3 w-3 rounded bg-pnlNegative" />
              <span>{t.routines.missed}</span>
            </div>
          </div>
        </div>
        <HeatmapGrid
          heatmapData={heatmapData}
          viewType={viewType}
          currentPeriod={currentPeriod}
          dayLetters={t.routines.dayLetters}
          monthNamesShort={t.routines.monthNamesShort}
        />
      </CardContent>
    </Card>
  );
}

function getDayColor(day: DayData): string {
  if (day.totalExpected === 0) return "bg-gray-200 dark:bg-gray-700";
  if (day.completionRate >= 0.9) return "bg-green-600";
  if (day.completionRate >= 0.7) return "bg-green-400";
  if (day.completionRate >= 0.3) return "bg-green-300";
  if (day.completionRate > 0) return "bg-green-200";
  return "bg-pnlNegative";
}

function getDayTooltip(day: DayData): string {
  const dateStr = day.date.toLocaleDateString("fr-FR", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
  if (day.totalExpected === 0) return `${dateStr} - Aucune routine attendue`;
  const rate = Math.round(day.completionRate * 100);
  return `${dateStr} - ${day.totalCompletions}/${day.totalExpected} routines complétées (${rate}%)`;
}

interface HeatmapGridProps {
  heatmapData: (DayData | null)[];
  viewType: ViewType;
  currentPeriod: { year: number; month: number; quarter: number };
  dayLetters: string[];
  monthNamesShort: string[];
}

function HeatmapGrid({
  heatmapData,
  viewType,
  currentPeriod,
  dayLetters,
  monthNamesShort,
}: HeatmapGridProps) {
  if (viewType === "month") {
    return <MonthHeatmap heatmapData={heatmapData} dayLetters={dayLetters} />;
  }
  return (
    <QuarterHeatmap
      heatmapData={heatmapData}
      currentPeriod={currentPeriod}
      dayLetters={dayLetters}
      monthNamesShort={monthNamesShort}
    />
  );
}

// Month view: days grouped in weeks with day-letter headers (L M M J V S D)
function MonthHeatmap({
  heatmapData,
  dayLetters,
}: {
  heatmapData: (DayData | null)[];
  dayLetters: string[];
}) {
  // Group days into weeks (rows of 7 starting Monday)
  // Build weeks: each week is an array of 7 slots (null = empty padding)
  const weeks: (DayData | null)[][] = [];
  let week: (DayData | null)[] = [];

  // Pad start: first day of data might not be Monday
  const firstDay = heatmapData.find((d) => d !== null);
  if (firstDay) {
    const dow = firstDay.date.getDay(); // 0=Sun
    const paddingStart = dow === 0 ? 6 : dow - 1; // slots before first day
    for (let i = 0; i < paddingStart; i++) week.push(null);
  }

  for (const day of heatmapData) {
    week.push(day);
    if (week.length === 7) {
      weeks.push(week);
      week = [];
    }
  }
  if (week.length > 0) {
    while (week.length < 7) week.push(null);
    weeks.push(week);
  }

  return (
    <div className="overflow-x-auto pb-2">
      {/* Day-letter header */}
      <div className="flex gap-1 mb-1">
        {dayLetters.map((letter, i) => (
          <div
            key={i}
            className="w-7 flex-shrink-0 text-center text-[10px] font-medium text-muted-foreground"
          >
            {letter}
          </div>
        ))}
      </div>
      {/* Weeks rows */}
      <div className="flex flex-col gap-1">
        {weeks.map((w, wi) => (
          <div key={wi} className="flex gap-1">
            {w.map((day, di) => {
              if (!day) {
                return <div key={di} className="w-7 h-7 flex-shrink-0" />;
              }
              return (
                <div
                  key={di}
                  className={cn(
                    "w-7 h-7 flex-shrink-0 rounded-sm flex items-center justify-center",
                    "text-[10px] font-medium text-white/80 transition-all hover:scale-110 cursor-pointer",
                    getDayColor(day),
                  )}
                  title={getDayTooltip(day)}
                >
                  {day.date.getDate()}
                </div>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
}

// Quarter view: months side-by-side with month label header
function QuarterHeatmap({
  heatmapData,
  currentPeriod,
  dayLetters,
  monthNamesShort,
}: {
  heatmapData: (DayData | null)[];
  currentPeriod: { year: number; month: number; quarter: number };
  dayLetters: string[];
  monthNamesShort: string[];
}) {
  const quarterStartMonth = currentPeriod.quarter * 3;

  // Split data into 3 months
  const months: (DayData | null)[][][] = [[], [], []]; // months[m] = weeks
  for (let m = 0; m < 3; m++) {
    const monthIndex = quarterStartMonth + m;
    const monthDays = heatmapData.filter(
      (d) => d !== null && d.date.getMonth() === monthIndex,
    ) as DayData[];

    // Build weeks for this month
    const weeks: (DayData | null)[][] = [];
    let week: (DayData | null)[] = [];

    const firstDay = monthDays[0];
    if (firstDay) {
      const dow = firstDay.date.getDay();
      const paddingStart = dow === 0 ? 6 : dow - 1;
      for (let i = 0; i < paddingStart; i++) week.push(null);
    }

    for (const day of monthDays) {
      week.push(day);
      if (week.length === 7) {
        weeks.push(week);
        week = [];
      }
    }
    if (week.length > 0) {
      while (week.length < 7) week.push(null);
      weeks.push(week);
    }

    months[m] = weeks;
  }

  return (
    <div className="overflow-x-auto pb-2">
      <div className="flex gap-6">
        {months.map((weeks, m) => {
          const monthIndex = quarterStartMonth + m;
          return (
            <div key={m} className="flex-shrink-0">
              {/* Month label */}
              <div className="text-xs font-semibold text-center mb-1 text-foreground">
                {monthNamesShort[monthIndex]}
              </div>
              {/* Day-letter header */}
              <div className="flex gap-1 mb-1">
                {dayLetters.map((letter, i) => (
                  <div
                    key={i}
                    className="w-6 flex-shrink-0 text-center text-[9px] font-medium text-muted-foreground"
                  >
                    {letter}
                  </div>
                ))}
              </div>
              {/* Weeks */}
              <div className="flex flex-col gap-1">
                {weeks.map((week, wi) => (
                  <div key={wi} className="flex gap-1">
                    {week.map((day, di) => {
                      if (!day) {
                        return (
                          <div key={di} className="w-6 h-6 flex-shrink-0" />
                        );
                      }
                      return (
                        <div
                          key={di}
                          className={cn(
                            "w-6 h-6 flex-shrink-0 rounded-sm flex items-center justify-center",
                            "text-[9px] font-medium text-white/80 transition-all hover:scale-110 cursor-pointer",
                            getDayColor(day),
                          )}
                          title={getDayTooltip(day)}
                        >
                          {day.date.getDate()}
                        </div>
                      );
                    })}
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function generateGlobalHeatmapData(
  routines: (TradingRoutine & { routineCompletions: RoutineCompletion[] })[],
  viewType: ViewType,
  year: number,
  month: number,
  quarter: number,
): (DayData | null)[] {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  // Calculate start and end dates based on view type
  let startDate: Date;
  let endDate: Date;

  if (viewType === "month") {
    startDate = new Date(year, month, 1);
    endDate = new Date(year, month + 1, 0); // Last day of month
  } else {
    // quarter
    const quarterStartMonth = quarter * 3;
    startDate = new Date(year, quarterStartMonth, 1);
    endDate = new Date(year, quarterStartMonth + 3, 0); // Last day of quarter
  }

  startDate.setHours(0, 0, 0, 0);
  endDate.setHours(23, 59, 59, 999);

  // Don't show future dates
  if (endDate > today) {
    endDate = today;
  }

  const days: (DayData | null)[] = [];
  const activeRoutines = routines.filter((r) => r.isActive);

  // Create a map of all completions by date
  const completionsByDate = new Map<string, number>();
  activeRoutines.forEach((routine) => {
    routine.routineCompletions.forEach((completion) => {
      const dateKey = getDateKey(new Date(completion.date));
      completionsByDate.set(
        dateKey,
        (completionsByDate.get(dateKey) || 0) + 1,
      );
    });
  });

  // Generate data for each day
  let currentDate = new Date(startDate);
  while (currentDate <= endDate) {
    const dateKey = getDateKey(currentDate);
    const totalCompletions = completionsByDate.get(dateKey) || 0;

    // Calculate how many routines are expected on this day
    let totalExpected = 0;
    activeRoutines.forEach((routine) => {
      if (isDateExpectedForRoutine(routine, currentDate, today)) {
        totalExpected++;
      }
    });

    const completionRate =
      totalExpected > 0 ? totalCompletions / totalExpected : 0;

    days.push({
      date: new Date(currentDate),
      totalCompletions,
      totalExpected,
      completionRate,
    });

    currentDate.setDate(currentDate.getDate() + 1);
  }

  return days;
}

function getDateKey(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function getPreferredDayOfWeek(
  routine: TradingRoutine & { routineCompletions: RoutineCompletion[] },
): number | null {
  if (routine.routineCompletions.length === 0) {
    return new Date(routine.createdAt).getDay();
  }

  const dayCounts = new Map<number, number>();
  routine.routineCompletions.forEach((completion) => {
    const day = new Date(completion.date).getDay();
    dayCounts.set(day, (dayCounts.get(day) || 0) + 1);
  });

  let maxCount = 0;
  let preferredDay: number | null = null;
  dayCounts.forEach((count, day) => {
    if (count > maxCount) {
      maxCount = count;
      preferredDay = day;
    }
  });

  return preferredDay;
}

function getPreferredDayOfMonth(
  routine: TradingRoutine & { routineCompletions: RoutineCompletion[] },
): number | null {
  if (routine.routineCompletions.length === 0) {
    return new Date(routine.createdAt).getDate();
  }

  const dayCounts = new Map<number, number>();
  routine.routineCompletions.forEach((completion) => {
    const day = new Date(completion.date).getDate();
    dayCounts.set(day, (dayCounts.get(day) || 0) + 1);
  });

  let maxCount = 0;
  let preferredDay: number | null = null;
  dayCounts.forEach((count, day) => {
    if (count > maxCount) {
      maxCount = count;
      preferredDay = day;
    }
  });

  return preferredDay;
}

function isDateExpectedForRoutine(
  routine: TradingRoutine & { routineCompletions: RoutineCompletion[] },
  date: Date,
  today: Date,
): boolean {
  if (date > today) {
    return false;
  }

  switch (routine.frequency) {
    case "daily":
      return true;

    case "weekly": {
      const preferredDay = getPreferredDayOfWeek(routine);
      if (preferredDay === null) {
        return false;
      }

      const dateDayOfWeek = date.getDay();

      const weekStart = new Date(date);
      const daysFromMonday = dateDayOfWeek === 0 ? 6 : dateDayOfWeek - 1;
      weekStart.setDate(weekStart.getDate() - daysFromMonday);
      weekStart.setHours(0, 0, 0, 0);

      const weekEnd = new Date(weekStart);
      weekEnd.setDate(weekEnd.getDate() + 6);
      weekEnd.setHours(23, 59, 59, 999);

      const completionInWeek = routine.routineCompletions.find(
        (completion) => {
          const completionDate = new Date(completion.date);
          return completionDate >= weekStart && completionDate <= weekEnd;
        },
      );

      if (completionInWeek) {
        const dateKey = getDateKey(date);
        const completionKey = getDateKey(new Date(completionInWeek.date));
        return dateKey === completionKey;
      }

      return dateDayOfWeek === preferredDay;
    }

    case "monthly": {
      const preferredDay = getPreferredDayOfMonth(routine);
      if (preferredDay === null) {
        return false;
      }

      const dateDayOfMonth = date.getDate();
      const dateMonth = date.getMonth();
      const dateYear = date.getFullYear();

      const lastDayOfMonth = new Date(dateYear, dateMonth + 1, 0).getDate();
      const effectivePreferredDay = Math.min(preferredDay, lastDayOfMonth);

      if (dateDayOfMonth !== effectivePreferredDay) {
        return false;
      }

      const monthStart = new Date(dateYear, dateMonth, 1);
      const monthEnd = new Date(dateYear, dateMonth + 1, 0);
      monthEnd.setHours(23, 59, 59, 999);

      const hasCompletionInMonth = routine.routineCompletions.some(
        (completion) => {
          const completionDate = new Date(completion.date);
          return completionDate >= monthStart && completionDate <= monthEnd;
        },
      );

      if (hasCompletionInMonth) {
        const dateKey = getDateKey(date);
        return routine.routineCompletions.some(
          (c) => getDateKey(new Date(c.date)) === dateKey,
        );
      }

      return true;
    }

    case "custom": {
      const thirtyDaysAgo = new Date(today);
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
      return date >= thirtyDaysAgo;
    }

    default:
      return false;
  }
}

